import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiFetch, ApiError } from '@/lib/api';
import { AuthShell, Divider, Field, GoogleIcon } from './SignInPage';

function safeReturnTo(raw: string | null, fallback = '/app'): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

export function SignUpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('return'));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch('/api/auth/sign-up/email', {
        method: 'POST',
        json: { name, email, password },
      });
      navigate('/verify-email', { replace: true, state: { email, returnTo } });
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.payload === 'object' && e.payload && 'message' in e.payload
          ? String((e.payload as { message: string }).message)
          : e instanceof Error
            ? e.message
            : 'Sign up failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function continueWithGoogle() {
    setError(null);
    setSubmitting(true);
    try {
      const data = await apiFetch<{ url?: string; redirect?: boolean }>(
        '/api/auth/sign-in/social',
        { method: 'POST', json: { provider: 'google', callbackURL: returnTo } },
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Google sign-in did not return a redirect URL.');
      }
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.payload === 'object' && e.payload && 'message' in e.payload
          ? String((e.payload as { message: string }).message)
          : e instanceof Error
            ? e.message
            : 'Google sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Free for the first 50 emails every month — forever.
      </p>

      <Button
        type="button"
        variant="outline"
        className="mt-8 w-full shadow-xs"
        onClick={continueWithGoogle}
        disabled={submitting}
      >
        <GoogleIcon />
        Continue with Google
      </Button>

      <Divider label="or sign up with email" />

      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Name">
          <Input
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-muted-foreground mt-1 text-xs">At least 8 characters.</p>
        </Field>
        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="text-muted-foreground mt-6 text-center text-sm">
        Already have an account?{' '}
        <Link
          to={`/sign-in${returnTo === '/app' ? '' : `?return=${encodeURIComponent(returnTo)}`}`}
          className="text-primary hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
